const CartPromotions = {
  running: false,

  init() {
    this.bindEvents();
    // Run after the theme cart listeners are ready.
    if (document.readyState === 'complete') {
      this.checkPromotions();
    } else {
      window.addEventListener('load', () => this.checkPromotions(), { once: true });
    }
  },

  bindEvents() {
    // Listen to standard cart update events in the theme
    const events = [
      'cart:item-added',
      'line-item:change:end',
      'cart-drawer:open',
      'cart:refresh'
    ];

    events.forEach(eventName => {
      document.addEventListener(eventName, () => {
        this.checkPromotions();
      });
    });
  },

  get rootUrl() {
    const shopifyRoutes = window.Shopify && window.Shopify.routes;
    const themeRoutes = window.theme && window.theme.routes;
    const root = (shopifyRoutes && shopifyRoutes.root) || (themeRoutes && themeRoutes.root_url) || '/';
    return root.endsWith('/') ? root : `${root}/`;
  },

  async checkPromotions() {
    if (this.running) return;
    this.running = true;

    try {
      const response = await fetch(`${this.rootUrl}cart?view=verify-promo`);

      if (!response.ok) {
        throw new Error(`Promo verification failed with status ${response.status}`);
      }

      const data = await response.json();
      let cartChanged = false;

      if (data.to_remove && data.to_remove.length > 0) {
        cartChanged = await this.removeGifts(data.to_remove) || cartChanged;
      }

      if (data.to_update && data.to_update.length > 0) {
        cartChanged = await this.updateGifts(data.to_update) || cartChanged;
      }

      if (data.to_add && data.to_add.length > 0) {
        cartChanged = await this.addGifts(data.to_add) || cartChanged;
      }

      if (cartChanged) {
        this.refreshCart();
      }
    } catch (error) {
      console.error('Error verifying cart promotions:', error);
    } finally {
      this.running = false;
    }
  },

  async addGifts(variantIds) {
    const items = variantIds.map(id => ({
      id: parseInt(id, 10),
      quantity: 1,
      properties: {
        '_is_free_gift': 'true'
      }
    }));

    try {
      const response = await fetch(`${this.rootUrl}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        throw new Error(`Failed to add promo gifts with status ${response.status}`);
      }
      return true;
    } catch (error) {
      console.error('Failed to add promo gifts:', error);
      return false;
    }
  },

  async removeGifts(itemKeys) {
    const updates = {};
    itemKeys.forEach(key => {
      updates[key] = 0;
    });

    try {
      await this.updateCart(updates);
      return true;
    } catch (error) {
      console.error('Failed to remove expired promo gifts:', error);
      return false;
    }
  },

  async updateGifts(items) {
    const updates = {};
    items.forEach(item => {
      if (item.key) {
        updates[item.key] = item.quantity || 1;
      }
    });

    try {
      await this.updateCart(updates);
      return true;
    } catch (error) {
      console.error('Failed to update promo gift quantities:', error);
      return false;
    }
  },

  async updateCart(updates) {
    const response = await fetch(`${this.rootUrl}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ updates })
    });

    if (!response.ok) {
      throw new Error(`Cart update failed with status ${response.status}`);
    }
  },

  refreshCart() {
    // Dispatch events to tell the theme to update the cart drawer, cart page, and header bubble
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

    // If we're on the cart page, reload is sometimes needed to update all components correctly
    if (window.location.pathname.endsWith('/cart')) {
      window.location.reload();
    }
  }
};

// Start initialization once DOM is ready or after window load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => CartPromotions.init());
} else {
  CartPromotions.init();
}
